// src/belediye/components/mesajlasma/sohbetKovalari.js
//
// FAZ-94 — SOHBET TÜRÜ VE SÜZGEÇ MANTIĞI, TEK OTORİTE.
//
// Bu kurallar FAZ-37..55 boyunca `ChatDock.js` içinde büyüdü. Telefon rotası
// (`pages/sohbet/*`) ve paylaşılan liste (`SohbetListesi.js`) da aynı kuralları
// okumak zorunda; ChatDock'tan almaları DÖNGÜSEL bağımlılık üretiyordu
// (ChatDock → SohbetListesi → ChatDock). Mantık buraya alındı.
//
// `ChatDock.js` hepsini yeniden dışa aktarıyor: sistemin başka yerlerinden ve
// testlerden gelen `import { convKind } from ".../ChatDock"` çağrıları tek
// satır bile değişmeden çalışır.

// Sohbet türü — satır renklendirmesi için: birebir / grup / görev / başvuru-görevi.
export const convKind = (c) =>
  c.kind === "dogrudan"
    ? "dm"
    : c.topic_ref
    ? c.topic_ref.application_tracking
      ? "basvuru"
      : "gorev"
    : "grup";

// ── Sohbet listesi segmentleri (chip'li sekmeler) ──────────────────────
// Kişi ve Gruplar / Görevler / Başvurular. Görevler kişi/grup sohbetlerinde
// ZATEN sabit şerit olarak beliriyor; görev sohbetleri yalnız "Görevler"
// sekmesinde. Başvuru sohbetleri yalnız "Başvurular" sekmesinde. Tamamlanan
// görev sohbeti Görevler sekmesinin "Tamamlananlar" alt-süzgecinde kalır.
export const MSJ_SEGMENTS_MAP = {
  kisi: { key: "kisi", label: "Kişi ve Gruplar" },
  gorev: { key: "gorev", label: "Talepler" },
  basvuru: { key: "basvuru", label: "Başvurular" },
};
// Geriye uyum: sabit 3'lü sıra (eski çağıranlar/testler için).
export const MSJ_SEGMENTS = Object.values(MSJ_SEGMENTS_MAP);

// FAZ-37: görünen sekme = açık özellik VEYA o türde HÂLÂ mevcut sohbet
// (salt-okunur — eski sohbet asla kaybolmaz). `locked` = özellik kapalı ama
// yalnız eski sohbet yüzünden görünüyor (chip'e kilit rozeti düşer).
export function visibleSegments(features, convs) {
  const feat = features || {};
  const has = (k) => (convs || []).some((c) => convKind(c) === k);
  const out = [];
  if (feat.serbest || has("dm") || has("grup")) {
    out.push({ ...MSJ_SEGMENTS_MAP.kisi, locked: !feat.serbest });
  }
  if (feat.gorev || has("gorev")) {
    out.push({ ...MSJ_SEGMENTS_MAP.gorev, locked: !feat.gorev });
  }
  if (feat.basvuru || has("basvuru")) {
    out.push({ ...MSJ_SEGMENTS_MAP.basvuru, locked: !feat.basvuru });
  }
  return out;
}

// FAZ-55/13 — GÖREVLER SEKMESİNİN ALT SÜZGECİ ÜÇE BÖLÜNDÜ.
// Kullanıcı kararı (2026-07-28): "aktif'i açık ile değiştirelim; açık, onay
// bekleyen ve tamamlanan şeklinde üç çip olsun. Açıkta yeni gidenler, daha işe
// başlanmamış olanlar, gecikenler ve benzerleri olsun."
//
// NEDEN üç: "onay bekliyor" ne açık iştir (iş bitmiş, teslim edilmiş) ne de
// kapalıdır (makam onaylamamış). İki kutulu düzende "Aktif"in içinde saklanıyor
// ve karar bekleyen işler gözden kaçıyordu — oysa bunlar EYLEM bekleyen,
// en görünür olması gereken satırlardır.
//
// Durum eşlemesi (internal_tasks_app.Task.STATUS_* ile birebir):
//   açık          → yeni | devam_ediyor | geri_bildirim_bekliyor  (+ gecikenler
//                   bunların içindedir; gecikme ayrı bir statü değil, termin
//                   türevidir — şeritte kırmızıya döner)
//   onay bekleyen → onay_bekliyor
//   tamamlanan    → tamamlandi | iptal_edildi (ve tanınmayan her statü)
const ACIK_GOREV_DURUM = ["yeni", "devam_ediyor", "geri_bildirim_bekliyor"];
const ONAY_GOREV_DURUM = ["onay_bekliyor"];
// Statü YOKSA (eski/eksik payload) güvenli taraf: AÇIK say — sohbet kaybolmasın.
const gorevDurumKovasi = (c) => {
  const st = c.topic_ref?.task_status;
  if (!st) return "acik";
  if (ACIK_GOREV_DURUM.includes(st)) return "acik";
  if (ONAY_GOREV_DURUM.includes(st)) return "onay";
  return "tamam";
};

export const GOREV_ALT_SUZGEC = [
  { key: "acik", label: "Açık" },
  { key: "onay", label: "Onay bekleyen" },
  { key: "tamam", label: "Tamamlanan" },
];

// Geriye uyum: eski "aktif" anahtarı "acik"in eş adıdır (dışarıdan çağıran
// kod/testler kırılmasın).
const altSuzgecNormalize = (k) => (k === "aktif" ? "acik" : k || "acik");

export const filterConvsBySegment = (convs, seg, altSuzgec = "acik") =>
  (convs || []).filter((c) => {
    const k = convKind(c); // dm | grup | gorev | basvuru
    if (seg === "gorev") {
      return k === "gorev" && gorevDurumKovasi(c) === altSuzgecNormalize(altSuzgec);
    }
    if (seg === "basvuru") return k === "basvuru";
    return k === "dm" || k === "grup"; // "kisi" (varsayılan): kişi + grup
  });

// Kova sayıları — süzgecin kendisiyle AYNI kuraldan türer (tek otorite).
export const gorevKovaSayilari = (convs) => {
  const out = { acik: 0, onay: 0, tamam: 0 };
  (convs || []).forEach((c) => {
    if (convKind(c) !== "gorev") return;
    out[gorevDurumKovasi(c)] += 1;
  });
  return out;
};
